import React, { useState, useEffect, useRef } from 'react';

const dark = {
  bg: '#0a0900',
  card: '#111008',
  cardAlt: '#0c0b02',
  border: '#3a2e00',
  gold: '#c9a84c',
  goldBright: '#f0c040',
  muted: '#7a6a40',
  mutedLight: '#9a8a60',
  text: '#fff',
  danger: '#e05c5c',
  success: '#6dbb88',
};

const btnBase = {
  border: 'none',
  borderRadius: 5,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '0.72rem',
  padding: '6px 14px',
};

const EarningsEditModal = ({ isOpen, onClose, record, companyId, onSave }) => {
  // Deep-clone sections into editable state
  const [sections, setSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [addingField, setAddingField] = useState(null); // sectionKey or null
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const backdropRef = useRef(null);

  useEffect(() => {
    if (isOpen && record) {
      setSections(JSON.parse(JSON.stringify(record.fields || {})));
      setError('');
      setAddingField(null);
    }
  }, [isOpen, record]);

  if (!isOpen || !record) return null;

  const updateField = (sectionKey, fieldKey, newVal) => {
    setSections((prev) => {
      const next = { ...prev };
      next[sectionKey] = { ...next[sectionKey], [fieldKey]: newVal };
      return next;
    });
  };

  const deleteField = (sectionKey, fieldKey) => {
    setSections((prev) => {
      const next = { ...prev };
      const sec = { ...next[sectionKey] };
      delete sec[fieldKey];
      next[sectionKey] = sec;
      return next;
    });
  };

  const renameField = (sectionKey, oldKey, newFieldKey) => {
    if (!newFieldKey || newFieldKey === oldKey) return;
    setSections((prev) => {
      const next = { ...prev };
      const sec = { ...next[sectionKey] };
      const val = sec[oldKey];
      delete sec[oldKey];
      sec[newFieldKey] = val;
      next[sectionKey] = sec;
      return next;
    });
  };

  const addField = (sectionKey) => {
    if (!newKey.trim()) return;
    const key = newKey.trim().toUpperCase().replace(/\s+/g, '_');
    setSections((prev) => {
      const next = { ...prev };
      next[sectionKey] = { ...next[sectionKey], [key]: newValue };
      return next;
    });
    setNewKey('');
    setNewValue('');
    setAddingField(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(companyId, record.period_end_date, sections);
      onClose();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  const sectionEntries = Object.entries(sections).filter(
    ([, val]) => val && typeof val === 'object' && !Array.isArray(val),
  );

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          backgroundColor: dark.bg,
          border: `1px solid ${dark.border}`,
          borderRadius: 10,
          width: '90vw',
          maxWidth: 900,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 18px',
            borderBottom: `1px solid ${dark.border}`,
            backgroundColor: dark.cardAlt,
          }}
        >
          <div>
            <span style={{ color: dark.goldBright, fontWeight: 700, fontSize: '0.85rem' }}>
              Edit Earnings Record
            </span>
            <span style={{ color: dark.muted, fontSize: '0.7rem', marginLeft: 10 }}>
              {record.period_end_date}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              ...btnBase,
              background: 'none',
              color: dark.muted,
              fontSize: '1rem',
              padding: '2px 8px',
            }}
          >
            &times;
          </button>
        </div>

        {/* Body — scrollable sections */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {sectionEntries.map(([sectionKey, sectionVal]) => {
            const entries = Object.entries(sectionVal);
            const sectionLabel =
              sectionKey === '_root'
                ? 'Root Fields'
                : sectionKey === '_meta'
                  ? 'Metadata'
                  : sectionKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

            return (
              <div key={sectionKey} style={{ marginBottom: 16 }}>
                {/* Section header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '5px 10px',
                    backgroundColor: dark.cardAlt,
                    borderRadius: 5,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: dark.goldBright,
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                    }}
                  >
                    {sectionLabel}
                  </span>
                  <button
                    onClick={() => setAddingField(addingField === sectionKey ? null : sectionKey)}
                    style={{
                      ...btnBase,
                      background: 'none',
                      color: dark.gold,
                      fontSize: '0.65rem',
                      padding: '2px 8px',
                    }}
                  >
                    <i className="bi bi-plus-lg me-1" />
                    Add Field
                  </button>
                </div>

                {/* Add new field row */}
                {addingField === sectionKey && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 6,
                      alignItems: 'center',
                      marginBottom: 6,
                      padding: '6px 8px',
                      backgroundColor: dark.card,
                      borderRadius: 4,
                      border: `1px solid ${dark.border}`,
                    }}
                  >
                    <input
                      placeholder="FIELD_NAME"
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      placeholder="Value"
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      style={inputStyle}
                    />
                    <button
                      onClick={() => addField(sectionKey)}
                      style={{ ...btnBase, backgroundColor: dark.gold, color: '#000' }}
                    >
                      Add
                    </button>
                    <button
                      onClick={() => { setAddingField(null); setNewKey(''); setNewValue(''); }}
                      style={{ ...btnBase, background: 'none', color: dark.muted }}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Field rows */}
                <div style={{ borderRadius: 5, overflow: 'hidden', border: `1px solid ${dark.border}` }}>
                  {entries.map(([fieldKey, fieldVal], idx) => (
                    <FieldRow
                      key={fieldKey}
                      fieldKey={fieldKey}
                      fieldVal={fieldVal}
                      even={idx % 2 === 0}
                      onUpdate={(val) => updateField(sectionKey, fieldKey, val)}
                      onDelete={() => deleteField(sectionKey, fieldKey)}
                      onRename={(newName) => renameField(sectionKey, fieldKey, newName)}
                    />
                  ))}
                  {entries.length === 0 && (
                    <div style={{ padding: '10px 12px', color: dark.muted, fontSize: '0.72rem' }}>
                      No fields in this section.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 18px',
            borderTop: `1px solid ${dark.border}`,
            backgroundColor: dark.cardAlt,
          }}
        >
          <div style={{ fontSize: '0.7rem', color: dark.danger }}>{error}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onClose}
              style={{ ...btnBase, backgroundColor: '#1a1800', color: dark.muted }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                ...btnBase,
                backgroundColor: saving ? dark.muted : dark.gold,
                color: '#000',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Editable field row ─────────────────────────────────────────────────────
const FieldRow = ({ fieldKey, fieldVal, even, onUpdate, onDelete, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState('');
  const [renamingKey, setRenamingKey] = useState(false);
  const [editKey, setEditKey] = useState('');

  const startEdit = () => {
    setEditVal(fieldVal !== null && fieldVal !== undefined ? String(fieldVal) : '');
    setEditing(true);
  };
  const commitEdit = () => {
    onUpdate(editVal);
    setEditing(false);
  };
  const startRename = () => {
    setEditKey(fieldKey);
    setRenamingKey(true);
  };
  const commitRename = () => {
    onRename(editKey.trim().toUpperCase().replace(/\s+/g, '_'));
    setRenamingKey(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 10px',
        backgroundColor: even ? dark.card : dark.cardAlt,
        minHeight: 36,
      }}
    >
      {/* Key */}
      <div style={{ flex: '0 0 40%', minWidth: 0 }}>
        {renamingKey ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input
              value={editKey}
              onChange={(e) => setEditKey(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && commitRename()}
              autoFocus
              style={{ ...inputStyle, flex: 1, fontSize: '0.65rem' }}
            />
            <button onClick={commitRename} style={iconBtn} title="Confirm">
              <i className="bi bi-check-lg" style={{ color: dark.success }} />
            </button>
            <button onClick={() => setRenamingKey(false)} style={iconBtn} title="Cancel">
              <i className="bi bi-x-lg" style={{ color: dark.muted }} />
            </button>
          </div>
        ) : (
          <span
            onDoubleClick={startRename}
            style={{
              fontSize: '0.65rem',
              color: dark.mutedLight,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={`Double-click to rename: ${fieldKey}`}
          >
            {fieldKey}
          </span>
        )}
      </div>

      {/* Value */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && commitEdit()}
              autoFocus
              style={{ ...inputStyle, flex: 1 }}
            />
            <button onClick={commitEdit} style={iconBtn} title="Confirm">
              <i className="bi bi-check-lg" style={{ color: dark.success }} />
            </button>
            <button onClick={() => setEditing(false)} style={iconBtn} title="Cancel">
              <i className="bi bi-x-lg" style={{ color: dark.muted }} />
            </button>
          </div>
        ) : (
          <span
            onDoubleClick={startEdit}
            style={{
              fontSize: '0.8rem',
              color: dark.text,
              fontWeight: 500,
              cursor: 'pointer',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={`Double-click to edit: ${fieldVal}`}
          >
            {fieldVal !== null && fieldVal !== '' && fieldVal !== undefined
              ? String(fieldVal)
              : <span style={{ color: dark.muted }}>—</span>}
          </span>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
        {!editing && !renamingKey && (
          <>
            <button onClick={startEdit} style={iconBtn} title="Edit value">
              <i className="bi bi-pencil" style={{ color: dark.gold, fontSize: '0.65rem' }} />
            </button>
            <button onClick={onDelete} style={iconBtn} title="Delete field">
              <i className="bi bi-trash" style={{ color: dark.danger, fontSize: '0.65rem' }} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const inputStyle = {
  backgroundColor: '#1a1800',
  border: `1px solid ${dark.border}`,
  borderRadius: 4,
  color: dark.text,
  fontSize: '0.75rem',
  padding: '4px 8px',
  outline: 'none',
};

const iconBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 4px',
  display: 'flex',
  alignItems: 'center',
};

export default EarningsEditModal;
